/**
 * Custom hook that reactively tracks window.location.pathname changes.
 * Works with SPAs (including Next.js) by patching history methods and listening to popstate.
 */
import { useState, useEffect } from 'react';

const useLocationPathname = () => {
    const [pathname, setPathname] = useState(() =>
        typeof window === 'undefined' ? '' : window.location.pathname,
    );

    useEffect(() => {
        // Patch history methods to dispatch custom events for SPA navigation
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;

        history.pushState = function (...args) {
            originalPushState.apply(this, args);
            window.dispatchEvent(new Event('locationchange'));
        };

        history.replaceState = function (...args) {
            originalReplaceState.apply(this, args);
            window.dispatchEvent(new Event('locationchange'));
        };

        const handleLocationChange = () => {
            setPathname(window.location.pathname);
        };

        // Listen to browser back/forward navigation
        window.addEventListener('popstate', handleLocationChange);
        // Listen to programmatic navigation (pushState/replaceState)
        window.addEventListener('locationchange', handleLocationChange);

        return () => {
            // Restore original methods on cleanup
            history.pushState = originalPushState;
            history.replaceState = originalReplaceState;
            window.removeEventListener('popstate', handleLocationChange);
            window.removeEventListener('locationchange', handleLocationChange);
        };
    }, []);

    return pathname;
};

export default useLocationPathname;
